import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import { GUIDES } from "@/content/guides";
import { guidesCopy } from "@/content/pages";
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, getTranslations, type Locale } from "@/i18n/translations";

const BASE_URL = "https://nellove.com";

const Guides = () => {
  const { locale } = useParams<{ locale: string }>();
  const currentLocale = (SUPPORTED_LOCALES.includes(locale as Locale) ? locale : DEFAULT_LOCALE) as Locale;
  const t = getTranslations(currentLocale);
  const copy = guidesCopy[currentLocale];
  const canonical = `${BASE_URL}/${currentLocale}/guides`;

  useEffect(() => {
    document.documentElement.lang = currentLocale;
  }, [currentLocale]);

  return (
    <SiteLayout locale={currentLocale} t={t} altPath={`/${currentLocale === "en" ? "fr" : "en"}/guides`}>
      <Helmet>
        <html lang={currentLocale} />
        <title>{copy.metaTitle}</title>
        <meta name="description" content={copy.metaDescription} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="en" href={`${BASE_URL}/en/guides`} />
        <link rel="alternate" hrefLang="fr" href={`${BASE_URL}/fr/guides`} />
        <link rel="alternate" hrefLang="x-default" href={`${BASE_URL}/en/guides`} />
        <meta property="og:title" content={copy.metaTitle} />
        <meta property="og:description" content={copy.metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={`${BASE_URL}/images/nellove-cover.png`} />
        <meta property="og:site_name" content="Nellove" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={copy.metaTitle} />
        <meta name="twitter:description" content={copy.metaDescription} />
        <meta name="twitter:image" content={`${BASE_URL}/images/nellove-cover.png`} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: copy.heading,
            description: copy.metaDescription,
            url: canonical,
            inLanguage: currentLocale === "fr" ? "fr-CA" : "en-CA",
            hasPart: GUIDES.map((g) => ({
              "@type": "Article",
              headline: g.content[currentLocale].title,
              description: g.content[currentLocale].description,
              url: `${BASE_URL}/${currentLocale}/guides/${g.content[currentLocale].slug}`,
            })),
          })}
        </script>
      </Helmet>

      <section className="border-b border-stone-200 bg-secondary px-5 py-14 sm:px-6 md:py-20">
        <div className="mx-auto max-w-3xl">
          <p className="text-[12px] font-medium tracking-[0.08em] text-stone-600">{copy.eyebrow}</p>
          <h1 className="mt-3 text-[36px] leading-[1.08] tracking-tight sm:text-[46px]">{copy.heading}</h1>
          <p className="mt-5 text-[17px] leading-relaxed text-stone-700">{copy.intro}</p>
        </div>
      </section>

      <section className="px-5 py-14 sm:px-6 md:py-16">
        <div className="mx-auto grid max-w-3xl gap-5">
          {GUIDES.map((guide) => {
            const body = guide.content[currentLocale];
            return (
              <article key={guide.id} className="rounded-xl border border-stone-200 bg-card p-6 shadow-soft">
                <h2 className="font-serif text-[24px] leading-snug tracking-tight">
                  <a
                    href={`/${currentLocale}/guides/${body.slug}`}
                    className="transition-colors duration-200 hover:text-rust-600"
                  >
                    {body.title}
                  </a>
                </h2>
                <p className="mt-3 text-[16px] leading-relaxed text-stone-600">{body.description}</p>
                <p className="mt-3 text-[13px] text-stone-600">{body.readingTime}</p>
                <a
                  href={`/${currentLocale}/guides/${body.slug}`}
                  className="mt-4 inline-flex items-center gap-2 text-[15px] font-medium text-rust-600 underline-offset-4 hover:underline"
                >
                  {copy.readMore}
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </a>
              </article>
            );
          })}

          <div className="rounded-xl border border-stone-200 bg-secondary p-6">
            <h2 className="font-serif text-[24px] tracking-tight">{copy.ctaHeading}</h2>
            <p className="mt-3 text-[16px] leading-relaxed text-stone-600">{copy.ctaBody}</p>
            <a
              href={`/${currentLocale}#join`}
              className="mt-5 inline-flex h-12 items-center gap-2 rounded-xl bg-primary px-6 text-base font-semibold text-primary-foreground transition-colors duration-200 hover:bg-rust-600"
            >
              {copy.ctaButton}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default Guides;
